import { useState } from 'react';
import { AsyncBoundary, Card, Drawer, Notice, useToast } from '@kollez/design-system';
import {
  useCampusInvitations,
  useRespondCampusInvitation,
  type CampusInvitationRow,
} from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /tpo/campus-invitations — inbox for companies asking to run a campus drive
 * with this college. Consumes GET /campus-invitations (scoped by TPO tenant)
 * + POST /campus-invitations/:id/respond. Both endpoints existed since the
 * CampusInvitation model shipped in Pass 8 but only the company sender surface
 * (POST create) was wired.
 *
 * The company creates one, the TPO gets a notification, the TPO decides here.
 * Decline requires a note (min 5 chars). Accept optionally carries context.
 */

const STATUS_TONE: Record<CampusInvitationRow['status'], string> = {
  sent: 'badge-info',
  viewed: 'badge',
  accepted: 'badge-success',
  declined: 'badge-warning',
  expired: 'badge',
};

function partnerName(inv: CampusInvitationRow): string {
  if (!inv.partner) return 'Unknown company';
  if (typeof inv.partner === 'string') return 'Company';
  return inv.partner.name ?? 'Company';
}

function opportunityTitle(inv: CampusInvitationRow): string | null {
  if (!inv.opportunity || typeof inv.opportunity === 'string') return null;
  return inv.opportunity.title ?? null;
}

function windowLabel(inv: CampusInvitationRow): string {
  const w = inv.preferredWindow;
  if (!w) return '—';
  const start = w.startsAt ? formatIndianDate(new Date(w.startsAt)) : null;
  const end = w.endsAt ? formatIndianDate(new Date(w.endsAt)) : null;
  if (w.label) return start && end ? `${w.label} · ${start} → ${end}` : w.label;
  if (start && end) return `${start} → ${end}`;
  return start ?? end ?? '—';
}

export function TpoCampusInvitationsPage() {
  const [status, setStatus] = useState<CampusInvitationRow['status'] | ''>('sent');
  const list = useCampusInvitations({ status, limit: 100 });
  const respond = useRespondCampusInvitation();
  const toast = useToast();
  const [opened, setOpened] = useState<CampusInvitationRow | null>(null);
  const [declineNote, setDeclineNote] = useState('');
  const [declineMode, setDeclineMode] = useState(false);

  const rows = list.data?.rows ?? [];
  const counts = { sent: 0, viewed: 0, accepted: 0, declined: 0, expired: 0 };
  for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;

  const openInv = (inv: CampusInvitationRow) => {
    setOpened(inv);
    setDeclineMode(false);
    setDeclineNote('');
  };

  const doAccept = () => {
    if (!opened) return;
    respond.mutate(
      { id: opened._id, action: 'accept' },
      {
        onSuccess: () => {
          setOpened(null);
          toast.show({ message: `Accepted ${partnerName(opened)}'s invitation.` });
        },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  const doDecline = () => {
    if (!opened) return;
    const note = declineNote.trim();
    if (note.length < 5) {
      toast.show({ tone: 'warning', message: 'Add a decline reason (min 5 chars).' });
      return;
    }
    respond.mutate(
      { id: opened._id, action: 'decline', note },
      {
        onSuccess: () => {
          setOpened(null);
          toast.show({ message: `Declined ${partnerName(opened)}'s invitation.` });
        },
        onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
      },
    );
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Partners</p>
          <h1 className="h-page">Campus invitations</h1>
          <p className="subhead">
            Companies asking to run a campus drive with your college. Accept to open the
            conversation, decline with a reason to close it.
          </p>
        </div>
      </div>

      <Notice tone="info">
        Accepting doesn't schedule anything on its own — it signals the company that you're
        interested. Follow up in the Partners page to set the drive date, MoU, and roles.
      </Notice>

      <Card padding="md">
        <div className="filterbar">
          <label className="cell-sub">Status</label>
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as CampusInvitationRow['status'] | '')}
          >
            <option value="">All</option>
            <option value="sent">Sent · new ({counts.sent})</option>
            <option value="viewed">Viewed ({counts.viewed})</option>
            <option value="accepted">Accepted ({counts.accepted})</option>
            <option value="declined">Declined ({counts.declined})</option>
            <option value="expired">Expired ({counts.expired})</option>
          </select>
          <span className="fb-count">{list.data?.total ?? rows.length}</span>
        </div>
      </Card>

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle={status === 'sent' ? 'No new invitations' : 'No invitations here'}
          emptyHint={status === 'sent'
            ? 'When a company asks to run a drive with your college, it lands here.'
            : 'Try a different status filter.'}
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Company</th>
                  <th className="hide-mobile">Role</th>
                  <th>Preferred window</th>
                  <th className="hide-mobile">Sent</th>
                  <th>Status</th>
                  <th className="r">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv._id}>
                    <td>
                      <div className="cell-title">{partnerName(inv)}</div>
                      {inv.note && (
                        <div className="cell-sub truncate" style={{ maxWidth: 380 }}>
                          {inv.note}
                        </div>
                      )}
                    </td>
                    <td className="hide-mobile cell-sub">{opportunityTitle(inv) ?? '—'}</td>
                    <td className="cell-sub">{windowLabel(inv)}</td>
                    <td className="hide-mobile cell-sub">
                      {inv.sentAt ? formatIndianDate(new Date(inv.sentAt)) : '—'}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_TONE[inv.status]}`}>{inv.status}</span>
                    </td>
                    <td className="r">
                      {inv.status === 'sent' || inv.status === 'viewed' ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => openInv(inv)}
                        >
                          Respond
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => openInv(inv)}
                        >
                          Open
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>

      <Drawer
        open={Boolean(opened)}
        onClose={() => setOpened(null)}
        title={opened ? partnerName(opened) : 'Campus invitation'}
        size="lg"
      >
        {opened && (
          <div className="stack">
            <div>
              <span className={`badge ${STATUS_TONE[opened.status]}`}>{opened.status}</span>
              {opportunityTitle(opened) && (
                <span className="badge ml-2">{opportunityTitle(opened)}</span>
              )}
            </div>

            <dl className="dl">
              <dt>Company</dt><dd>{partnerName(opened)}</dd>
              <dt>Preferred window</dt><dd>{windowLabel(opened)}</dd>
              <dt>Sent</dt><dd>{opened.sentAt ? formatIndianDate(new Date(opened.sentAt)) : '—'}</dd>
              {opened.respondedAt && (
                <>
                  <dt>Responded</dt><dd>{formatIndianDate(new Date(opened.respondedAt))}</dd>
                </>
              )}
            </dl>

            {opened.note && (
              <div>
                <div className="eyebrow mb-1">Company's note</div>
                <p className="m-0" style={{ whiteSpace: 'pre-wrap' }}>{opened.note}</p>
              </div>
            )}

            {opened.responseNote && (
              <div>
                <div className="eyebrow mb-1">Your response</div>
                <p className="m-0" style={{ whiteSpace: 'pre-wrap' }}>{opened.responseNote}</p>
              </div>
            )}

            {(opened.status === 'sent' || opened.status === 'viewed') && !declineMode && (
              <div className="drawer-foot flex justify-end gap-2 flex-wrap">
                <button
                  className="btn btn-ghost btn-danger"
                  onClick={() => setDeclineMode(true)}
                  disabled={respond.isPending}
                >
                  Decline…
                </button>
                <button
                  className="btn btn-primary"
                  onClick={doAccept}
                  disabled={respond.isPending}
                >
                  {respond.isPending ? 'Sending…' : 'Accept'}
                </button>
              </div>
            )}

            {(opened.status === 'sent' || opened.status === 'viewed') && declineMode && (
              <div className="stack">
                <label className="block">
                  <span className="eyebrow">Decline reason (min 5 chars)</span>
                  <textarea
                    className="input mt-1"
                    rows={3}
                    value={declineNote}
                    onChange={(e) => setDeclineNote(e.target.value)}
                    placeholder="e.g. Semester exams overlap this window. Can revisit in Nov."
                  />
                </label>
                <div className="flex justify-end gap-2 flex-wrap">
                  <button
                    className="btn btn-ghost"
                    onClick={() => setDeclineMode(false)}
                    disabled={respond.isPending}
                  >
                    Back
                  </button>
                  <button
                    className="btn btn-primary btn-danger"
                    onClick={doDecline}
                    disabled={respond.isPending || declineNote.trim().length < 5}
                  >
                    {respond.isPending ? 'Sending…' : 'Decline'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
